// Lightweight i18n for Stillerom (NO) / Stillroom (EN)
// No external dependencies — single dictionary, persistent locale, browser-fallback.
import { useEffect, useState, useCallback } from 'react';

const LANG_KEY = 'stillerom_lang';
const SUPPORTED = ['no', 'en'];

export function detectInitialLang() {
    // Path-based override: /en/* always = en
    if (typeof window !== 'undefined') {
        if (window.location.pathname.startsWith('/en')) return 'en';
        const saved = localStorage.getItem(LANG_KEY);
        if (saved && SUPPORTED.includes(saved)) return saved;
        const nav = (navigator.language || 'no').toLowerCase();
        if (nav.startsWith('no') || nav.startsWith('nb') || nav.startsWith('nn') || nav.startsWith('da') || nav.startsWith('sv')) return 'no';
        return 'en';
    }
    return 'no';
}

const dict = {
    // App-wide
    'app.brand.no': { no: 'Stillerom', en: 'Stillroom' },
    'app.tagline': { no: 'Et stille rom for samtaler du trenger.', en: 'A place to talk when there’s no one else.' },
    'common.start': { no: 'Start', en: 'Start' },
    'common.cancel': { no: 'Avbryt', en: 'Cancel' },
    'common.save': { no: 'Lagre', en: 'Save' },
    'common.continue': { no: 'Fortsett', en: 'Continue' },
    'common.delete': { no: 'Slett', en: 'Delete' },
    'common.confirm': { no: 'Bekreft', en: 'Confirm' },
    'common.loading': { no: 'Laster…', en: 'Loading…' },
    'common.error': { no: 'Noe gikk galt', en: 'Something went wrong' },
    'common.send': { no: 'Send', en: 'Send' },
    'common.close': { no: 'Lukk', en: 'Close' },

    // Sidebar
    'nav.dashboard': { no: 'Oversikt', en: 'Today' },
    'nav.chats': { no: 'Samtaler', en: 'Conversations' },
    'nav.mood': { no: 'Humør', en: 'Mood' },
    'nav.breathing': { no: 'Pustepauser', en: 'Breathing' },
    'nav.checkin': { no: 'Innsjekking', en: 'Check-in' },
    'nav.resources': { no: 'Ressurser', en: 'Resources' },
    'nav.settings': { no: 'Konto', en: 'Account' },
    'nav.logout': { no: 'Logg ut', en: 'Sign out' },
    'nav.admin': { no: 'Admin', en: 'Admin' },
    'nav.admin.analytics': { no: 'Analytics', en: 'Analytics' },
    'nav.admin.feedback': { no: 'Tilbakemeldinger', en: 'Feedback' },

    // Auth
    'auth.welcome': { no: 'Velkommen til Stillerom', en: 'Welcome to Stillroom' },
    'auth.subtitle': { no: 'Logg inn eller fortsett anonymt.', en: 'Sign in, or just start talking.' },
    'auth.continue.anonymous': { no: 'Fortsett anonymt – 3 dager gratis', en: 'Just start talking — 3 days free' },
    'auth.anonymous.subtle': { no: 'Ingen e-post. Ingen konto. Bare en samtale.', en: 'No email. No account. Just a conversation.' },
    'auth.email': { no: 'E-post', en: 'Email' },
    'auth.password': { no: 'Passord', en: 'Password' },
    'auth.signin': { no: 'Logg inn', en: 'Sign in' },
    'auth.create.account': { no: 'Opprett konto', en: 'Create account' },
    'auth.or': { no: 'eller', en: 'or' },
    'auth.continue.google': { no: 'Fortsett med Google', en: 'Continue with Google' },
    'auth.have.account': { no: 'Har du allerede konto?', en: 'Already have an account?' },
    'auth.no.account': { no: 'Ny her?', en: 'New here?' },

    // Dashboard
    'dash.greeting': { no: 'God dag, {name}.', en: 'Hello, {name}.' },
    'dash.title': { no: 'Hva trenger du i dag?', en: 'What do you need today?' },
    'dash.new.chat': { no: 'Ny samtale', en: 'New conversation' },
    'dash.continue.chat': { no: 'Fortsett samtale', en: 'Continue conversation' },
    'dash.continue.chat.empty': { no: 'Ingen ennå', en: 'None yet' },
    'dash.log.mood': { no: 'Logg humør', en: 'Log mood' },
    'dash.log.mood.desc': { no: 'Ta pulsen på hvordan det står til', en: 'Take your emotional temperature' },
    'dash.breathing': { no: 'Pustepause', en: 'Breathing' },
    'dash.breathing.desc': { no: '4-7-8 eller boks-pust', en: '4-7-8 or box breathing' },
    'dash.recent.chats': { no: 'Nylige samtaler', en: 'Recent conversations' },
    'dash.see.all': { no: 'Se alle', en: 'See all' },
    'dash.empty.chats': { no: 'Ingen samtaler ennå. Start en når du er klar.', en: 'No conversations yet. Start one whenever you’re ready.' },
    'dash.crisis.title': { no: 'Trenger du akutt hjelp?', en: 'In crisis right now?' },
    'dash.crisis.body.no': { no: 'Hvis det haster, ring Mental Helse 116 123 (Norge) – døgnåpent.', en: '' },
    'dash.crisis.body.en': { no: '', en: 'If this is urgent, call or text 988 — the Suicide & Crisis Lifeline. 24/7, free, confidential.' },
    'dash.crisis.btn': { no: 'Se alle hjelpelinjer', en: 'See all hotlines' },
    'dash.mood.recent': { no: 'Humør siste dager', en: 'Mood — recent days' },

    // Chats
    'chats.title': { no: 'Dine samtaler', en: 'Your conversations' },
    'chats.new': { no: 'Ny', en: 'New' },
    'chats.empty': { no: 'Ingen samtaler ennå.', en: 'Nothing here yet.' },
    'chats.empty.cta': { no: 'Start din første samtale', en: 'Start your first conversation' },
    'chats.delete.confirm': { no: 'Slette denne samtalen?', en: 'Delete this conversation?' },
    'chats.delete.success': { no: 'Samtale slettet', en: 'Conversation deleted' },

    // New chat dialog
    'launcher.title': { no: 'Start ny samtale', en: 'Start a conversation' },
    'launcher.subtitle': { no: 'Velg hvilken type samtalepartner du ønsker, og hvor dypt vi skal gå.', en: 'Pick how it should listen, and how deep we go.' },
    'launcher.persona.label': { no: 'Type samtalepartner', en: 'Conversation style' },
    'launcher.depth.label': { no: 'Dybde', en: 'Depth' },
    'launcher.depth.1': { no: 'Lett', en: 'Light' },
    'launcher.depth.2': { no: 'Balansert', en: 'Balanced' },
    'launcher.depth.3': { no: 'Dypere', en: 'Deeper' },
    'launcher.depth.4': { no: 'Eksistensiell', en: 'Existential' },
    'launcher.depth.1.desc': { no: 'Uformell, korte svar', en: 'Casual, short replies' },
    'launcher.depth.2.desc': { no: 'Følger ditt tempo', en: 'Matches your pace' },
    'launcher.depth.3.desc': { no: 'Utforsker mønstre og antakelser', en: 'Explores patterns and assumptions' },
    'launcher.depth.4.desc': { no: 'Verdier, mening, tilknytning', en: 'Values, meaning, attachment' },
    'launcher.start': { no: 'Start samtale', en: 'Start talking' },
    'launcher.starting': { no: 'Starter…', en: 'Starting…' },

    // Settings page
    'settings.title': { no: 'Konto', en: 'Account' },
    'settings.language': { no: 'Språk', en: 'Language' },
    'settings.language.no': { no: 'Norsk (Bokmål)', en: 'Norwegian (Bokmål)' },
    'settings.language.en': { no: 'Engelsk', en: 'English' },
    'settings.signed.in.as': { no: 'Innlogget som', en: 'Signed in as' },
    'settings.export': { no: 'Last ned mine data (GDPR)', en: 'Download my data' },
    'settings.delete': { no: 'Slett kontoen min', en: 'Delete my account' },
    'settings.cancel.subscription': { no: 'Si opp abonnement', en: 'Cancel subscription' },

    // Crisis modal
    'crisis.title.no': { no: 'Hvis det haster:', en: '' },
    'crisis.title.en': { no: '', en: 'If this is an emergency:' },

    // PWA install
    'install.title': { no: 'Installer Stillerom', en: 'Install Stillroom' },
    'install.body': { no: 'Få rask tilgang fra hjemskjermen din.', en: 'One-tap access from your home screen.' },
    'install.btn': { no: 'Installer', en: 'Install' },
    'install.dismiss': { no: 'Ikke nå', en: 'Not now' },

    // Settings — extended
    'settings.auth.method': { no: 'Innlogging:', en: 'Sign-in:' },
    'settings.auth.google': { no: 'Google', en: 'Google' },
    'settings.auth.anonymous': { no: 'Anonym', en: 'Anonymous' },
    'settings.auth.email': { no: 'E-post', en: 'Email' },
    'settings.payment.processing': { no: 'Behandler betalingen…', en: 'Processing your payment…' },
    'settings.pilot.title': { no: '🙏 Er du pilottester?', en: '🙏 Are you a pilot tester?' },
    'settings.pilot.body': { no: 'Tilbakemeldingene dine former hvordan Stillerom utvikler seg. Tar ca. 3 minutter.', en: 'Your feedback shapes how Stillroom evolves. Takes ~3 minutes.' },
    'settings.pilot.btn': { no: 'Gi tilbakemelding', en: 'Give feedback' },
    'settings.membership': { no: 'Medlemskap', en: 'Membership' },
    'settings.plan.active': { no: 'Aktiv plan', en: 'Active plan' },
    'settings.plan.basic': { no: 'Basic', en: 'Basic' },
    'settings.plan.premium': { no: 'Premium', en: 'Premium' },
    'settings.plan.trial': { no: 'Gratis Premium (3 dager)', en: 'Free Premium (3 days)' },
    'settings.plan.until': { no: 'til', en: 'until' },
    'settings.trial.hours.left': { no: 'timer igjen', en: 'hours left' },
    'settings.cancel.confirm': { no: 'Er du sikker på at du vil avslutte abonnementet? Du beholder tilgangen ut gjeldende periode.', en: 'Are you sure you want to cancel your subscription? You keep access until the end of the current period.' },
    'settings.cancel.scheduled': { no: 'Abonnement avsluttes ved periode-slutt', en: 'Subscription will end at period end' },
    'settings.cancel.scheduled.notice': { no: 'Abonnementet avsluttes ved periodens slutt. Du beholder tilgangen til da.', en: 'Your subscription ends at the end of the period. You keep access until then.' },
    'settings.cancel.busy': { no: 'Avslutter…', en: 'Cancelling…' },
    'settings.cancel.btn': { no: 'Avslutt abonnement ved periode-slutt', en: 'Cancel at period end' },
    'settings.cancel.error': { no: 'Kunne ikke avslutte abonnement', en: 'Could not cancel subscription' },
    'settings.payment.success': { no: 'Betaling fullført – velkommen som medlem!', en: 'Payment complete — welcome aboard!' },
    'settings.payment.expired': { no: 'Betalings-økten utløp', en: 'Payment session expired' },
    'settings.payment.error': { no: 'Kunne ikke starte betaling', en: 'Could not start payment' },
    'settings.trial.offer.title': { no: '3 dager Premium – helt gratis', en: '3 days of Premium — free' },
    'settings.trial.offer.body': { no: 'Ingen kort, ingen forpliktelse. Full tilgang i 72 timer.', en: 'No card, no commitment. Full access for 72 hours.' },
    'settings.trial.activated': { no: '3 dager Premium er aktivert', en: '3 days of Premium activated' },
    'settings.trial.error': { no: 'Kunne ikke starte gratisperioden', en: 'Could not start the free trial' },
    'settings.voice.heading': { no: 'Stemme for opplesning', en: 'Voice for read-aloud' },
    'settings.voice.subtitle': { no: 'Prøv dem – velg den som føles trygg.', en: 'Try them — pick the one that feels safe.' },
    'settings.voice.female': { no: 'Kvinnelig', en: 'Female' },
    'settings.voice.male': { no: 'Mannlig', en: 'Male' },
    'settings.voice.neutral': { no: 'Nøytral', en: 'Neutral' },
    'settings.voice.preview': { no: 'Forhåndsvis', en: 'Preview' },
    'settings.voice.selected': { no: 'Valgt', en: 'Selected' },
    'settings.voice.select': { no: 'Velg', en: 'Choose' },
    'settings.voice.updated': { no: 'Stemme oppdatert', en: 'Voice updated' },
    'settings.voice.preview.error': { no: 'Kunne ikke spille av stemmen', en: 'Could not play voice' },
    'settings.logout': { no: 'Logg ut', en: 'Sign out' },
    'settings.plan.choose': { no: 'Velg', en: 'Choose' },
    'settings.plan.active.label': { no: 'Aktiv', en: 'Active' },
    'settings.plan.opening': { no: 'Åpner…', en: 'Opening…' },
    'settings.plan.basic.feat1': { no: '30 min samtale per dag', en: '30 min talking per day' },
    'settings.plan.basic.feat2': { no: 'Alle 5 samtalestiler', en: 'Stillroom voice' },
    'settings.plan.basic.feat3': { no: 'Diktering og opplesning', en: 'Voice in & out' },
    'settings.plan.premium.feat1': { no: 'Ubegrenset samtaler og tid', en: 'Unlimited' },
    'settings.plan.premium.feat2': { no: 'Full samtalehistorikk lagret', en: 'Full history saved' },
    'settings.plan.premium.feat3': { no: 'Prioritert svar-tid', en: 'Priority replies' },

    // Privacy / data card
    'privacy.title': { no: 'Dine data', en: 'Your data' },
    'privacy.body': { no: 'Du eier alt du deler på Stillerom. Du kan laste ned alle samtalene og dataene dine når som helst, eller slette hele kontoen permanent.', en: 'You own everything you share on Stillroom. You can download all your conversations and data anytime, or permanently delete your account.' },
    'privacy.export.btn': { no: 'Last ned alle mine data (JSON)', en: 'Download all my data (JSON)' },
    'privacy.export.busy': { no: 'Eksporterer…', en: 'Exporting…' },
    'privacy.export.success': { no: 'Eksport lastet ned', en: 'Export downloaded' },
    'privacy.export.error': { no: 'Eksport feilet', en: 'Export failed' },
    'privacy.delete.btn': { no: 'Slett konto og alle data', en: 'Delete account and all data' },
    'privacy.delete.warn': { no: 'Dette kan ikke angres.', en: 'This cannot be undone.' },
    'privacy.delete.body': { no: 'Alle samtaler, humør-loggen, innsjekk, betalingshistorikk og kontoen din slettes permanent. Et aktivt abonnement blir også kansellert umiddelbart hos Stripe.', en: 'All conversations, mood log, check-ins, payment history, and your account are permanently deleted. An active subscription will also be cancelled immediately at Stripe.' },
    'privacy.delete.type': { no: "Skriv 'SLETT' nedenfor for å bekrefte:", en: "Type 'DELETE' below to confirm:" },
    'privacy.delete.placeholder': { no: 'SLETT', en: 'DELETE' },
    'privacy.delete.confirm.word': { no: 'SLETT', en: 'DELETE' },
    'privacy.delete.confirm.error': { no: "Skriv 'SLETT' for å bekrefte", en: "Type 'DELETE' to confirm" },
    'privacy.delete.confirm.btn': { no: 'Slett permanent', en: 'Delete permanently' },
    'privacy.delete.busy': { no: 'Sletter…', en: 'Deleting…' },
    'privacy.delete.success': { no: 'Kontoen og alle data er slettet', en: 'Account and all data deleted' },
    'privacy.delete.error': { no: 'Sletting feilet', en: 'Deletion failed' },

    // Link email card
    'linkemail.title': { no: 'Koble til en e-post – uten å miste historikken', en: 'Link an email — without losing your history' },
    'linkemail.body': { no: 'Anonyme kontoer finnes kun på denne enheten. Legg til en e-post og et passord hvis du vil kunne logge inn fra en annen enhet eller beholde kontoen ved nettleser-tømming. Samtaler, humør og innsjekk blir med videre.', en: 'Anonymous accounts only exist on this device. Add an email and password if you want to sign in from another device or keep the account if your browser is cleared. Conversations, mood, and check-ins come along.' },
    'linkemail.open.btn': { no: 'Koble til e-post', en: 'Link an email' },
    'linkemail.alias': { no: 'Alias (valgfritt)', en: 'Alias (optional)' },
    'linkemail.email': { no: 'E-post', en: 'Email' },
    'linkemail.password': { no: 'Passord (min. 6 tegn)', en: 'Password (min. 6 characters)' },
    'linkemail.password.short': { no: 'Passord må være minst 6 tegn', en: 'Password must be at least 6 characters' },
    'linkemail.busy': { no: 'Kobler til…', en: 'Linking…' },
    'linkemail.success': { no: 'E-post koblet til kontoen – historikken er bevart', en: 'Email linked — your history is preserved' },
    'linkemail.error': { no: 'Kunne ikke koble til e-post', en: 'Could not link email' },

    // Chat view
    'chat.back': { no: 'Samtaler', en: 'Conversations' },
    'chat.adjust.title': { no: 'Juster samtalen', en: 'Adjust the conversation' },
    'chat.adjust.desc': { no: 'Bytt stil på samtalen.', en: 'Change the conversation style.' },
    'chat.style': { no: 'Stil', en: 'Style' },
    'chat.depth': { no: 'Dybde', en: 'Depth' },
    'chat.depth.1': { no: 'Lett', en: 'Light' },
    'chat.depth.2': { no: 'Balansert', en: 'Balanced' },
    'chat.depth.3': { no: 'Dypere', en: 'Deeper' },
    'chat.depth.4': { no: 'Eksistensiell', en: 'Existential' },
    'chat.apply': { no: 'Bruk', en: 'Apply' },
    'chat.input.placeholder': { no: 'Skriv eller diktér…', en: 'Write or dictate…' },
    'chat.input.recording': { no: 'Opptak pågår – trykk stopp når du er ferdig', en: 'Recording — tap stop when you’re done' },
    'chat.input.transcribing': { no: 'Transkriberer…', en: 'Transcribing…' },
    'chat.record.start': { no: 'Diktér', en: 'Dictate' },
    'chat.record.stop': { no: 'Stopp opptak', en: 'Stop recording' },
    'chat.mic.denied': { no: 'Mikrofon-tilgang ble avvist', en: 'Microphone access was denied' },
    'chat.transcribe.error': { no: 'Kunne ikke transkribere', en: 'Could not transcribe' },
    'chat.load.error': { no: 'Kunne ikke laste samtalen', en: 'Could not load this conversation' },
    'chat.send.error': { no: 'Noe gikk galt', en: 'Something went wrong' },
    'chat.updated': { no: 'Samtale oppdatert', en: 'Conversation updated' },
    'chat.style.changed': { no: 'Stil justert', en: 'Style adjusted' },
    'chat.summary.title': { no: 'Oppsummering', en: 'Summary' },
    'chat.summary.desc': { no: 'En kort oppsummering av samtalen.', en: 'A short summary of the conversation.' },
    'chat.summary.generating': { no: 'Genererer…', en: 'Generating…' },
    'chat.summary.error': { no: 'Kunne ikke generere oppsummering.', en: 'Could not generate summary.' },
    'chat.you': { no: 'Du', en: 'You' },
    'chat.tts.loading': { no: 'Laster…', en: 'Loading…' },
    'chat.tts.stop': { no: 'Stopp', en: 'Stop' },
    'chat.tts.read': { no: 'Les opp', en: 'Read aloud' },
    'chat.tts.error': { no: 'Kunne ikke spille av lyd', en: 'Could not play audio' },
    'chat.copy.blocked.title': { no: 'Lesemodus i prøveperioden', en: 'Read-only during trial' },
    'chat.copy.blocked.body': { no: 'Kopiering er deaktivert for å beskytte samtalen. Oppgrader for å kopiere fritt.', en: 'Copying is disabled to protect the conversation. Upgrade for full access.' },

    // Password change
    'password.section.title': { no: 'Endre passord', en: 'Change password' },
    'password.current': { no: 'Nåværende passord', en: 'Current password' },
    'password.new': { no: 'Nytt passord (min. 6 tegn)', en: 'New password (min. 6 characters)' },
    'password.confirm': { no: 'Bekreft nytt passord', en: 'Confirm new password' },
    'password.submit': { no: 'Lagre nytt passord', en: 'Save new password' },
    'password.busy': { no: 'Lagrer…', en: 'Saving…' },
    'password.changed': { no: 'Passordet er oppdatert', en: 'Password updated' },
    'password.too.short': { no: 'Passordet må være minst 6 tegn', en: 'Password must be at least 6 characters' },
    'password.mismatch': { no: 'De to passordene må være like', en: 'The two passwords must match' },
    'password.change.error': { no: 'Kunne ikke endre passord', en: 'Could not change password' },
    'password.must.change.title': { no: 'Velg ditt eget passord', en: 'Choose your own password' },
    'password.must.change.body': { no: 'Du har et midlertidig passord. Velg ditt eget før du fortsetter — du forblir innlogget.', en: 'You have a temporary password. Choose your own before continuing — you stay signed in.' },
    'chat.style.nudge': { no: 'Vil du bytte stil?', en: 'Want to change style?' },
    'chat.save.nudge.q': { no: 'Vil du fortsette dette senere?', en: 'Want to continue this later?' },
    'chat.save.nudge.body': { no: 'Denne samtalen kan lagres og fortsettes i Premium.', en: 'This conversation can be saved and continued in Premium.' },
    'chat.save.nudge.cta': { no: 'Lagre', en: 'Save' },

    // Limit modal
    'limit.trial.title': { no: 'Du har funnet noe som fungerer.', en: 'You’ve found something that works.' },
    'limit.trial.body.lead': { no: 'Få 3 dager Premium gratis', en: 'Get 3 days of Premium free' },
    'limit.trial.body.tail': { no: '– ingen kort nødvendig, ingen forpliktelse. Full tilgang i 72 timer.', en: '— no card needed, no commitment. Full access for 72 hours.' },
    'limit.trial.feat1': { no: 'Ubegrenset samtaler', en: 'Unlimited conversations' },
    'limit.trial.feat2': { no: 'Diktering og opplesning', en: 'Voice in & out' },
    'limit.trial.feat3': { no: 'Hele historikken din lagret', en: 'Your full history saved' },
    'limit.later': { no: 'Senere', en: 'Later' },
    'limit.start.trial': { no: 'Start 3 dager gratis', en: 'Start 3 days free' },
    'limit.trial.activated': { no: '3 dager Premium aktivert', en: '3 days of Premium activated' },
    'limit.upgrade.title': { no: 'Denne samtalen er for god til å miste.', en: 'This conversation is too good to lose.' },
    'limit.upgrade.body': { no: 'Denne samtalen kan lagres og fortsettes i Premium – pluss du får ubegrenset chat med samme samtalepartner.', en: 'This conversation can be saved and continued in Premium — plus unlimited chat with the same companion.' },
    'limit.upgrade.see': { no: 'Se medlemskap', en: 'See membership' },

    // Progress strip
    'progress.trial.urgent': { no: '⚠ Gratisperioden utløper om {h}t – historikken forsvinner', en: '⚠ Free trial ends in {h}h — history will be lost' },
    'progress.trial.normal': { no: 'Gratis Premium · {h} timer igjen', en: 'Free Premium · {h} hours left' },
    'progress.basic.over': { no: 'Dagens 30 minutter er brukt · vi ses i morgen', en: 'Today’s 30 minutes are used · see you tomorrow' },
    'progress.basic.left': { no: 'Dagens samtale · {m} minutter igjen', en: 'Today’s conversation · {m} minutes left' },
    'progress.free.over': { no: 'Gratis-grensen er nådd', en: 'Free limit reached' },
    'progress.free.left.one': { no: 'Du har {n} gratis melding igjen', en: 'You have {n} free message left' },
    'progress.free.left.many': { no: 'Du har {n} gratis meldinger igjen', en: 'You have {n} free messages left' },
};

let _currentLang = typeof window !== 'undefined' ? detectInitialLang() : 'no';
const _listeners = new Set();

export function getLang() { return _currentLang; }

export function setLang(lang) {
    if (!SUPPORTED.includes(lang)) return;
    _currentLang = lang;
    try { localStorage.setItem(LANG_KEY, lang); } catch {}
    document.documentElement.lang = lang;
    _listeners.forEach((fn) => fn(lang));
    // Persist to backend (best-effort, never blocks UI)
    if (typeof window !== 'undefined' && localStorage.getItem('skeptiker_token')) {
        import('./api').then(({ api }) => {
            api.put('/me/language', { language: lang }).catch(() => {});
        }).catch(() => {});
    }
}

export function t(key, vars) {
    const entry = dict[key];
    if (!entry) return key;
    let s = entry[_currentLang] ?? entry.no ?? key;
    if (vars) {
        for (const [k, v] of Object.entries(vars)) {
            s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        }
    }
    return s;
}

export function useT() {
    const [lang, setLangState] = useState(_currentLang);
    useEffect(() => {
        const fn = (l) => setLangState(l);
        _listeners.add(fn);
        return () => _listeners.delete(fn);
    }, []);
    const change = useCallback((l) => setLang(l), []);
    return { t, lang, setLang: change };
}
